import { createContext, useContext, useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "./api";
import audioManager from "./audioManager";
import { toast } from "sonner";

/**
 * Iter-27.46 — Mode Podcast
 *
 * Context global qui orchestre la lecture audio enchaînée de toutes les slides
 * de toutes les leçons d'un module. Le mini-player est rendu une fois dans
 * `App.jsx` et persiste pendant la navigation.
 *
 * API publique :
 *   const { start, stop, togglePause, skipNext, skipPrev, state, currentItem } = usePodcast();
 *
 * State exposé :
 *   - isPlaying       : boolean (audio en cours)
 *   - isLoading       : boolean (chargement audio current)
 *   - moduleTitle     : string (titre du module en cours)
 *   - moduleCover     : string (image cover pour le mini-player)
 *   - items           : array d'items {lesson_id, lesson_title, slide_idx, slide_label}
 *   - currentIdx      : index dans items[]
 *   - totalSlides     : items.length
 *   - estimatedMinutes: durée estimée du module
 */

const PodcastContext = createContext(null);

export function usePodcast() {
    const ctx = useContext(PodcastContext);
    if (!ctx) throw new Error("usePodcast must be used inside <PodcastProvider>");
    return ctx;
}

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";

export function PodcastProvider({ children }) {
    const navigate = useNavigate();
    const [moduleId, setModuleId] = useState(null);
    const [moduleTitle, setModuleTitle] = useState("");
    const [moduleCover, setModuleCover] = useState(null);
    const [items, setItems] = useState([]);
    const [currentIdx, setCurrentIdx] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [estimatedMinutes, setEstimatedMinutes] = useState(0);
    // Iter-27.48 — Progression dans l'audio courant (pour seekbar)
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    // Iter-27.50 — Vitesse de lecture client-side (0.8x / 1x / 1.25x / 1.5x).
    // Persistée en localStorage pour respecter la préférence apprenant entre sessions.
    const [playbackRate, setPlaybackRateState] = useState(() => {
        try {
            const stored = parseFloat(localStorage.getItem("shariti:podcast-rate"));
            if ([0.8, 1, 1.25, 1.5].includes(stored)) return stored;
        } catch { /* */ }
        return 1.0;
    });
    const audioRef = useRef(null);
    const currentIdxRef = useRef(0);
    const itemsRef = useRef([]);
    // Iter-30.54 — moduleId stable pour les callbacks qui marquent les leçons.
    const moduleIdRef = useRef(null);
    // Iter-30.54 — Tracking des leçons déjà /start et /complete pour rester
    // idempotent (on ne re-poste pas si l'utilisateur skip back/forward).
    const startedLessonsRef = useRef(new Set());
    const completedLessonsRef = useRef(new Set());
    // Iter-27.47 — Token générationnel + audioManager handle (anti-chevauchement)
    const playbackTokenRef = useRef(0);
    const audioHandleRef = useRef(null);
    if (!audioHandleRef.current) {
        audioHandleRef.current = audioManager.acquire("podcast");
    }
    useEffect(() => { currentIdxRef.current = currentIdx; }, [currentIdx]);
    useEffect(() => { itemsRef.current = items; }, [items]);
    useEffect(() => { moduleIdRef.current = moduleId; }, [moduleId]);

    // Iter-30.54 — Marque une leçon comme complétée côté backend.
    // - Idempotent (Set en mémoire empêche les doubles POST si l'user
    //   skip/seek/rewind plusieurs fois)
    // - Émet un CustomEvent que Module.jsx et Parcours.jsx écoutent pour
    //   rafraîchir leur état de progression sans reload.
    const markLessonCompleted = useCallback(async (lessonId) => {
        const mid = moduleIdRef.current;
        if (!mid || !lessonId) return;
        if (completedLessonsRef.current.has(lessonId)) return;
        completedLessonsRef.current.add(lessonId);
        try {
            await api.post(`/modules/${mid}/lessons/${lessonId}/complete?source=podcast`);
            window.dispatchEvent(
                new CustomEvent("shariti:podcast-lesson-completed", {
                    detail: { moduleId: mid, lessonId },
                }),
            );
        } catch {
            // Si /complete échoue (ex: min_seconds non atteint car podcast très
            // rapide), on retire du Set pour autoriser une nouvelle tentative
            // plus tard (ex: à la fin du podcast, ou lors d'un rejeu).
            completedLessonsRef.current.delete(lessonId);
        }
    }, []);

    // Iter-27.47 — Si une autre source (LessonPresentation) prend la main, on
    // arrête proprement le podcast côté UI.
    useEffect(() => {
        const unsub = audioManager.subscribe((evt) => {
            if (evt.type === "stopped-by-other" && evt.ownerId === "podcast") {
                playbackTokenRef.current += 1;
                setIsPlaying(false);
                audioRef.current = null;
            }
        });
        return unsub;
    }, []);

    const fetchAndPlayItem = useCallback(async (item) => {
        // Iter-27.47 — Token générationnel : abort tout fetch + audio précédent
        playbackTokenRef.current += 1;
        const myToken = playbackTokenRef.current;
        if (audioRef.current) {
            try { audioRef.current.pause(); audioRef.current.src = ""; } catch { /* */ }
            audioRef.current = null;
        }
        setIsLoading(true);
        setIsPlaying(false);

        // Iter-30.54 — Marque la leçon comme démarrée (idempotent) pour
        // satisfaire la vérification `min_seconds` côté backend lors du
        // futur /complete.
        const mid = moduleIdRef.current;
        if (mid && item?.lesson_id && !startedLessonsRef.current.has(item.lesson_id)) {
            startedLessonsRef.current.add(item.lesson_id);
            api.post(`/modules/${mid}/lessons/${item.lesson_id}/start`).catch(() => { /* */ });
        }

        try {
            const res = await fetch(
                `${API_BASE}/lessons/${encodeURIComponent(item.lesson_id)}/slide/${item.slide_idx}/audio`,
                { credentials: "include" },
            );
            if (!res.ok) throw new Error("audio failed");
            if (myToken !== playbackTokenRef.current) return;
            const blob = await res.blob();
            if (myToken !== playbackTokenRef.current) return;
            const audio = new Audio(URL.createObjectURL(blob));
            audio.onended = () => {
                if (myToken !== playbackTokenRef.current) return;
                // Iter-30.54 — Si la slide qui vient de se terminer est la
                // DERNIÈRE de sa leçon (next item a un lesson_id différent OU
                // c'est le dernier item du podcast), on marque la leçon comme
                // complétée côté backend → progression et déverrouillage du
                // module suivant.
                const justFinished = itemsRef.current[currentIdxRef.current];
                const upcoming = itemsRef.current[currentIdxRef.current + 1];
                const isLastOfLesson =
                    justFinished &&
                    (!upcoming || upcoming.lesson_id !== justFinished.lesson_id);
                if (isLastOfLesson && justFinished?.lesson_id) {
                    markLessonCompleted(justFinished.lesson_id);
                }
                // Auto-skip vers l'item suivant
                const next = currentIdxRef.current + 1;
                if (next < itemsRef.current.length) {
                    setCurrentIdx(next);
                    fetchAndPlayItem(itemsRef.current[next]);
                } else {
                    setIsPlaying(false);
                    // Iter-30.55 — CTA "Passer au quiz" à la fin du podcast
                    // pour réduire le drop-off entre l'écoute audio et la
                    // validation du quiz (= déverrouillage réel du module).
                    const finishedModuleId = moduleIdRef.current;
                    toast.success("Module terminé en podcast 🎧", {
                        description: "Place au quiz pour valider et débloquer la suite.",
                        duration: 9000,
                        action: finishedModuleId
                            ? {
                                  label: "Passer au quiz",
                                  onClick: () => {
                                      try {
                                          navigate(`/module/${finishedModuleId}?tab=quiz`);
                                      } catch { /* */ }
                                  },
                              }
                            : undefined,
                    });
                    // Iter-30.54 — Émet aussi un event "podcast-finished" pour
                    // que Module.jsx puisse proposer le quiz / module suivant.
                    try {
                        window.dispatchEvent(
                            new CustomEvent("shariti:podcast-finished", {
                                detail: { moduleId: finishedModuleId },
                            }),
                        );
                    } catch { /* */ }
                    stopInternal();
                }
            };
            audio.onpause = () => {
                if (myToken !== playbackTokenRef.current) return;
                if (audio.currentTime < audio.duration - 0.1) {
                    setIsPlaying(false);
                }
            };
            audio.onplay = () => {
                if (myToken !== playbackTokenRef.current) return;
                setIsPlaying(true);
            };
            // Iter-27.48 — Suivi de la progression pour le seekbar
            audio.onloadedmetadata = () => {
                if (myToken !== playbackTokenRef.current) return;
                setDuration(audio.duration || 0);
            };
            audio.ontimeupdate = () => {
                if (myToken !== playbackTokenRef.current) return;
                setCurrentTime(audio.currentTime || 0);
            };
            audioHandleRef.current.attach(audio);
            audioRef.current = audio;
            // Iter-27.50 — Applique la vitesse client-side avant le 1er play
            try { audio.playbackRate = playbackRate; } catch { /* */ }
            if (myToken !== playbackTokenRef.current) {
                try { audio.pause(); audio.src = ""; } catch { /* */ }
                return;
            }
            await audio.play();
            if (myToken !== playbackTokenRef.current) {
                try { audio.pause(); audio.src = ""; } catch { /* */ }
                return;
            }
            setIsPlaying(true);
        } catch {
            if (myToken === playbackTokenRef.current) {
                toast.error("Erreur de lecture audio");
                setIsPlaying(false);
            }
        } finally {
            if (myToken === playbackTokenRef.current) setIsLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const stopInternal = useCallback(() => {
        playbackTokenRef.current += 1;
        if (audioRef.current) {
            try { audioRef.current.pause(); audioRef.current.src = ""; } catch { /* */ }
            audioRef.current = null;
        }
        if (audioHandleRef.current) audioHandleRef.current.release();
        setModuleId(null);
        setModuleTitle("");
        setModuleCover(null);
        setItems([]);
        setCurrentIdx(0);
        setIsPlaying(false);
        setIsLoading(false);
        // Iter-30.54 — Vide les Sets de suivi pour qu'un nouveau lancement
        // du podcast sur le même module re-tente proprement les /start et
        // /complete (utile si l'user a relancé pour ré-écouter).
        startedLessonsRef.current = new Set();
        completedLessonsRef.current = new Set();
    }, []);

    const start = useCallback(async (mod_id) => {
        try {
            stopInternal();
            const { data } = await api.get(`/modules/${mod_id}/podcast/plan`);
            if (!data?.items?.length) {
                toast.error("Ce module n'a pas encore de contenu audio.");
                return;
            }
            setModuleId(mod_id);
            setModuleTitle(data.module_title);
            setModuleCover(data.module_cover || null);
            setItems(data.items);
            setEstimatedMinutes(data.estimated_minutes || 0);
            setCurrentIdx(0);
            setCurrentTime(0);
            setDuration(0);
            toast.success(`🎧 Mode podcast lancé — ${data.estimated_minutes} min d'écoute`);
            await fetchAndPlayItem(data.items[0]);
        } catch {
            toast.error("Impossible de démarrer le podcast");
        }
    }, [fetchAndPlayItem, stopInternal]);

    const togglePause = useCallback(() => {
        if (!audioRef.current) return;
        if (audioRef.current.paused) {
            // Iter-27.47 — Re-acquire the audio control before resuming
            // (au cas où une autre source a pris la main entre-temps)
            audioHandleRef.current.attach(audioRef.current);
            audioRef.current.play().catch(() => { /* */ });
        } else {
            audioRef.current.pause();
        }
    }, []);

    const skipNext = useCallback(() => {
        const next = currentIdxRef.current + 1;
        if (next >= itemsRef.current.length) return;
        setCurrentIdx(next);
        fetchAndPlayItem(itemsRef.current[next]);
    }, [fetchAndPlayItem]);

    const skipPrev = useCallback(() => {
        const prev = currentIdxRef.current - 1;
        if (prev < 0) return;
        setCurrentIdx(prev);
        fetchAndPlayItem(itemsRef.current[prev]);
    }, [fetchAndPlayItem]);

    const stop = useCallback(() => {
        stopInternal();
        setCurrentTime(0);
        setDuration(0);
        toast("Podcast arrêté");
    }, [stopInternal]);

    // Iter-27.48 — Seek dans l'audio courant (drag/click sur le seekbar)
    const seek = useCallback((seconds) => {
        if (!audioRef.current || !isFinite(seconds)) return;
        try {
            audioRef.current.currentTime = Math.max(0, Math.min(seconds, audioRef.current.duration || 0));
            setCurrentTime(audioRef.current.currentTime);
        } catch { /* */ }
    }, []);

    // Iter-27.50 — Change la vitesse de lecture (client-side). Effet immédiat
    // sur l'audio en cours + persistance pour les prochains items.
    const setPlaybackRate = useCallback((rate) => {
        const safe = [0.8, 1, 1.25, 1.5].includes(rate) ? rate : 1.0;
        setPlaybackRateState(safe);
        try {
            localStorage.setItem("shariti:podcast-rate", String(safe));
        } catch { /* */ }
        if (audioRef.current) {
            try { audioRef.current.playbackRate = safe; } catch { /* */ }
        }
    }, []);

    const value = {
        moduleId,
        moduleTitle,
        moduleCover,
        items,
        currentIdx,
        currentItem: items[currentIdx] || null,
        totalSlides: items.length,
        estimatedMinutes,
        isPlaying,
        isLoading,
        isActive: !!moduleId,
        currentTime,
        duration,
        playbackRate,
        start,
        stop,
        togglePause,
        skipNext,
        skipPrev,
        seek,
        setPlaybackRate,
    };

    return <PodcastContext.Provider value={value}>{children}</PodcastContext.Provider>;
}
